import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import type { Asset } from '@khadi/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLocations, useModels, useSuppliers } from '../lib/queries';

/** Gerätedaten korrigieren. Die Zuweisung läuft bewusst über „Übergeben". */
export function AssetEditSheet({ asset, onClose }: { asset: Asset; onClose: () => void }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const models = useModels();
  const locations = useLocations();
  const suppliers = useSuppliers();

  const [form, setForm] = useState({
    serial: asset.serial,
    model_id: asset.model?.id ?? 0,
    home_location_id: asset.home_location?.id ?? 0,
    supplier_id: asset.supplier?.id ?? 0,
    purchase_date: asset.purchase_date ?? '',
    purchase_cost: asset.purchase_cost ?? '',
    order_number: asset.order_number ?? '',
    datev_id: asset.datev_id ?? '',
    notes: asset.notes ?? '',
  });

  const save = useMutation({
    mutationFn: () =>
      api<Asset>(`/assets/${asset.id}`, {
        method: 'PATCH',
        body: {
          serial: form.serial.trim(),
          model_id: form.model_id || undefined,
          home_location_id: form.home_location_id || null,
          supplier_id: form.supplier_id || null,
          purchase_date: form.purchase_date || null,
          purchase_cost: form.purchase_cost ? Number(form.purchase_cost) : null,
          order_number: form.order_number.trim() || null,
          datev_id: form.datev_id.trim() || null,
          notes: form.notes.trim() || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['asset', asset.id] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Änderungen gespeichert');
      onClose();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen'),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--color-khadi-forest-900)]/45 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Gerät bearbeiten"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up safe-bottom max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-t-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-bg-elevated)] p-5 shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-[family-name:var(--font-display)] text-2xl">
            {asset.asset_tag} bearbeiten
          </h2>
          <button type="button" onClick={onClose} className="btn btn-quiet btn-sm" aria-label="Schließen">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="label" htmlFor="edit-serial">
                Seriennummer
              </label>
              <input
                id="edit-serial"
                className="field tabular"
                value={form.serial}
                onChange={(event) => setForm({ ...form, serial: event.target.value })}
                required
              />
            </div>

            <div className="sm:col-span-2">
              <label className="label" htmlFor="edit-model">
                Modell
              </label>
              <select
                id="edit-model"
                className="field"
                value={form.model_id}
                onChange={(event) => setForm({ ...form, model_id: Number(event.target.value) })}
              >
                <option value={0}>— kein Modell —</option>
                {models.data?.rows.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="edit-location">
                Standort
              </label>
              <select
                id="edit-location"
                className="field"
                value={form.home_location_id}
                onChange={(event) =>
                  setForm({ ...form, home_location_id: Number(event.target.value) })
                }
              >
                <option value={0}>— keiner —</option>
                {locations.data?.rows.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="edit-supplier">
                Lieferant
              </label>
              <select
                id="edit-supplier"
                className="field"
                value={form.supplier_id}
                onChange={(event) => setForm({ ...form, supplier_id: Number(event.target.value) })}
              >
                <option value={0}>— keiner —</option>
                {suppliers.data?.rows.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="edit-purchase-date">
                Kaufdatum
              </label>
              <input
                id="edit-purchase-date"
                type="date"
                className="field"
                value={form.purchase_date}
                onChange={(event) => setForm({ ...form, purchase_date: event.target.value })}
              />
            </div>

            {auth.can('cost.read') && (
              <div>
                <label className="label" htmlFor="edit-cost">
                  Kaufpreis (€)
                </label>
                <input
                  id="edit-cost"
                  type="number"
                  step="0.01"
                  min="0"
                  className="field tabular"
                  value={form.purchase_cost}
                  onChange={(event) => setForm({ ...form, purchase_cost: event.target.value })}
                />
              </div>
            )}

            <div>
              <label className="label" htmlFor="edit-order">
                Bestellnummer
              </label>
              <input
                id="edit-order"
                className="field"
                value={form.order_number}
                onChange={(event) => setForm({ ...form, order_number: event.target.value })}
              />
            </div>

            <div>
              <label className="label" htmlFor="edit-datev">
                Datev-Anlagengut
              </label>
              <input
                id="edit-datev"
                className="field tabular"
                value={form.datev_id}
                onChange={(event) => setForm({ ...form, datev_id: event.target.value })}
                placeholder="Anlagennummer"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="label" htmlFor="edit-notes">
                Notizen
              </label>
              <textarea
                id="edit-notes"
                rows={3}
                className="field"
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn btn-ghost btn-lg flex-1">
              Abbrechen
            </button>
            <button type="submit" className="btn btn-primary btn-lg flex-1" disabled={save.isPending}>
              {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
